import { DeleteConfirmationDialog } from '@components/dialogs/DeleteConfirmationDialog'
import { DataTable } from '@components/tables'
import { DialogTitle } from '@components/titles'
import {
  Container,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
} from '@mui/material'
import { GridColumns, GridRowId } from '@mui/x-data-grid-pro'
import { Actions } from '@state/actions'
import { useSelector } from '@state/store'
import { findAllRooms, removeWaitingRooms } from '@state/thunks/roomThunk'
import { findAll as findAllSites } from '@state/thunks/siteThunk'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { RoomType, Site, WaitingRoom } from '@services/api'
import { removeSites } from '@state/reducers/siteReducer'
import { WaitingRoomsForm } from './WaitingRoomsForm'
import { EditIcon } from 'lucide-react'

export default function WaitingRooms() {
  const dispatch = useDispatch()
  const [openAddDialog, setOpenAddDialog] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [editingWaitingRoom, setEditingWaitingRoom] = useState<
    WaitingRoom | undefined
  >(undefined)

  const { loading, rooms } = useSelector(({ room, loading }) => ({
    rooms: room.rooms.filter((_room) => _room.type === RoomType.WaitingRoom),
    loading:
      loading[Actions.UPDATE_WAITING_ROOM] ||
      loading[Actions.CREATE_WAITING_ROOM] ||
      loading[Actions.ROOM_FIND_ALL],
  }))

  const columns: GridColumns = [
    { field: 'code', headerName: 'Code', flex: 1 },
    { field: 'label', headerName: 'Label', flex: 1 },
    {
      field: 'site',
      headerName: 'Site',
      flex: 1,
      valueFormatter: (params) => {
        return (params.value as unknown as Site)?.label
      },
    },
    {
      field: 'reception',
      headerName: 'Reception',
      flex: 1,
      valueFormatter: (params) => {
        return (params.value as unknown as any)?.label
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'right',
      renderCell: (params) => {
        return (
          <IconButton
            size="small"
            onClick={() => {
              setEditingWaitingRoom(params.row)
              setOpenAddDialog(true)
            }}
          >
            <EditIcon />
          </IconButton>
        )
      },
    },
  ]

  const onRefresh = useCallback(() => {
    dispatch(findAllRooms())
    dispatch(findAllSites())
  }, [dispatch])

  useEffect(() => {
    onRefresh()
    return () => {
      dispatch(removeSites())
    }
  }, [dispatch, onRefresh])

  const _onDeleteConfirm = () => {
    dispatch(removeWaitingRooms({ id: selectedRows }))
    setIsDeleteDialogOpen(false)
  }

  const _onSelectionChange = (selection: GridRowId[]) => {
    setSelectedRows(selection as number[])
  }

  return (
    <Container maxWidth={false} style={{ height: '93%', padding: '0px 24px' }}>
      <Paper
        elevation={2}
        style={{ width: '100%', borderRadius: '0px 15px 15px 15px' }}
      >
        <DataTable
          rows={rooms}
          columns={columns}
          loading={loading}
          toolBarProps={{
            title: 'waitingRooms',
            icon: 'fab fa-uikit',
            onAdd: () => setOpenAddDialog(true),
            onRefresh,
            onDelete: () => {
              selectedRows.length
                ? setIsDeleteDialogOpen(true)
                : alert('please select at least one waiting-room')
            },
          }}
          detailPanelHeight={400}
          checkboxSelection
          onSelectionModelChange={_onSelectionChange}
        />
      </Paper>
      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        maxWidth="lg"
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          title="addWaitingRoom"
          onClose={() => setOpenAddDialog(false)}
          format
        />
        <DialogContent>
          <WaitingRoomsForm
            onClose={() => setOpenAddDialog(false)}
            row={editingWaitingRoom}
          />
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={_onDeleteConfirm}
        title="Confirmer la suppression"
        description="Etes vous sur de vouloir supprimer les salles d'attente selectionnées ?"
      />
    </Container>
  )
}
